/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SalesOrderApiResponse } from "../../../@types/DRO/SalesOrderResponse";
import { ReturnOrderApi } from "../../../api/OrderManagement/ReturnOrderApi";
import { UseDebounce } from "../../../states/Hooks/UseDebounce";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import SalesOrderReturnTableComponent from "../../Layouts/Table/SalesOrderReturnTableComponent";
import CustomDropdown from "../CustomDropdown/CustomDropdown";

const salesReturnStatusOptions = [
  { value: "", label: "ALL" },
  { value: "PENDING", label: "Pending" },
  { value: "CLOSED", label: "Closed" },
];

const ReturnOrderPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLoading } = useLoading();
  const columns = [
    "id",
    "reference",
    "origin",
    "customerName",
    "cycleNo",
    "creationDate",
    "warehouseName",
    "warehouseCode",
    "jobId",
    "jobType",
    "fleetName",
    "status",
    "action",
  ];
  const columnHeaders = [
    "Sales Order ID",
    "Reference",
    "Origin",
    "Customer Name",
    "Cycle No",
    "Creation Date",
    "Warehouse Name",
    "Warehouse Code",
    "Job ID",
    "Job Type",
    "Fleet Name",
    "Status",
    "Action",
  ];
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const debouncedSearch = UseDebounce(search, 500);

  // Update URL when status filter changes
  useEffect(() => {
    if (statusFilter) {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);
  const debouncedCustomerFilter = UseDebounce(customerFilter, 500);
  const [totalPages, setTotalPages] = useState(1);
  const [salesOrders, setSalesOrders] = useState<
    SalesOrderApiResponse["data"]["data"]
  >([]);
  const [selectedOrder, setSelectedOrder] = useState<{
    id: number;
    status: string;
  } | null>(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, debouncedCustomerFilter, statusFilter]);

  useEffect(() => {
    const fetchReturnOrders = async () => {
      setLoading(true);
      try {
        const response = await ReturnOrderApi.api.getAllReturnOrders(
          page,
          pageSize,
          debouncedSearch,
          "RETURN",
          statusFilter,
          debouncedCustomerFilter
        );
        setSalesOrders(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching sales orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReturnOrders();
  }, [page, pageSize, debouncedSearch, statusFilter, debouncedCustomerFilter]);

  const handleViewOrder = (orderId: number) => {
    navigate(`/sales-order/return/view/${orderId}${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  const formattedSalesOrders = salesOrders.map((order) => ({
    id: order.id,
    reference: order.reference,
    origin: order.origin || "-",
    customerName: order.customerName || "N/A",
    cycleNo: order.cycleNo || "-",
    creationDate: order.creationDate,
    warehouseName:
      order.fromWarehouseDTO?.deliveryWarehouseName ||
      order.toWarehouseDTO?.deliveryWarehouseName ||
      "N/A",
    warehouseCode:
      order.fromWarehouseDTO?.deliveryWarehouseCode ||
      order.toWarehouseDTO?.deliveryWarehouseCode ||
      "N/A",
    jobId: order.jobId || "N/A",
    jobType: order.jobType || "N/A",
    fleetName: order.fleetName || "N/A",
    status: order.status || "N/A",
    action: order.id,
  }));

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar - Fixed width */}
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>

      {/* Main content area - Takes remaining space */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto bg-[#F8FAFC]">
          <div className="p-6">
            <h1 className="text-3xl font-semibold mb-6 text-left">
              Sales Order Return
            </h1>

            {/* Header with filters */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                {/* Search Bar for reference/warehouse */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by Order ID, Origin"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-60 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {/* Customer Name Filter */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by customer name , cycle no"
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="pl-8 pr-4 py-2 border border-gray-300 rounded-lg w-60 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {/* Status Filter */}
                <CustomDropdown
                  label="Status:"
                  options={salesReturnStatusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  minWidth="120px"
                />
              </div>
            </div>

            {/* Table Container with horizontal scroll */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <SalesOrderReturnTableComponent
                  columns={columns}
                  columnHeaders={columnHeaders}
                  data={formattedSalesOrders}
                  setSelectedOrder={setSelectedOrder}
                  currentPage={page}
                  setCurrentPage={setPage}
                  totalPages={totalPages}
                  statusFilter={statusFilter}
                  onViewOrder={handleViewOrder}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnOrderPage;
